// src/hooks/data/useSpaces.ts
import { useState, useEffect, useCallback } from 'react';
import { Space } from '../../../../types';
import { fetchSpaces } from '../../../../services/api/endpoints/spaces';
import { store } from '../../../../services/data/store';

// We no longer need to extend the store at runtime since we've updated the store type

interface UseSpacesResult {
  spaces: Space[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useSpaces = (token: string | null): UseSpacesResult => {
  const [error, setError] = useState<string | null>(null);
  
  // We'll use a state variable to trigger re-renders when the store updates
  const [, setUpdateTrigger] = useState<number>(0);

  // Load spaces data
  const loadSpaces = useCallback(async () => {
    if (!token) return;
    
    try {
      // Update loading state in store
      store.metadata.isLoading.spaces = true;
      // Force component to re-render with updated loading state
      setUpdateTrigger(Date.now());
      
      const spacesData = await fetchSpaces(token);
      
      // Update store with spaces data
      spacesData.forEach(space => {
        store.entities.spaces[space.id] = space;
      });
      
      // Update metadata
      store.metadata.isLoading.spaces = false;
      store.metadata.lastFetched.spaces = Date.now();
      
      // Force component to re-render with updated data
      setUpdateTrigger(Date.now());
      setError(null);
    } catch (err) {
      console.error('Error loading spaces:', err);
      setError('Failed to load spaces');
      store.metadata.isLoading.spaces = false;
      setUpdateTrigger(Date.now());
    }
  }, [token]);

  // Initial data loading
  useEffect(() => {
    if (token) {
      // Only load if we haven't loaded recently or don't have any spaces
      const lastFetched = store.metadata.lastFetched.spaces || 0;
      const hasSpaces = Object.keys(store.entities.spaces).length > 0;
      const needsRefresh = Date.now() - lastFetched > 5 * 60 * 1000; // 5 minutes
      
      if (!hasSpaces || needsRefresh) {
        loadSpaces();
      }
    }
  }, [token, loadSpaces]);

  // Get spaces from the store
  const spaces = Object.values(store.entities.spaces);
  const isLoading = store.metadata.isLoading.spaces;

  return {
    spaces,
    isLoading,
    error,
    refresh: loadSpaces
  };
};

export default useSpaces;